import { useCallback, useEffect, useRef, useState } from "react";
import { isEmpty, uniq, flatten, uniqWith, isEqual, zip } from "lodash";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LSubTitle } from "../../../common/components/LSubtitle";
import { concatenateBlobs } from "../../../common/helpers/blobHelper";
import { sleepFor, useCountDown } from "../../../common/helpers/countHelper";
import { useApi } from "../../../services/useApi";
import { AssessmentShell } from "../components/AssessmentShell";
import { useCustomSpeechToText } from "../hooks/useCustomSpeechToText";
import { playAudioAsync, useAudio } from "../hooks/useAudio";
import { useNavigate } from "react-router-dom";
import { NavItems } from "../../../common/helpers/navigation";
import {
    CategorizedWords,
    filterAndMatch,
    flattenAndCategorize,
    getUniqueWords,
    getWordsAndAlternatesFromList
} from "../../../common/helpers/instructionHelper";
import { ResultType } from "../../../utils/stt/useSpeechToText";
import { Flow, updateCurrentFlow } from "../../../common/store/assessmentFlowSlice";
import { Word } from "../../../common/store/assessmentSlice";
import { getLocale, translate, Translate } from "react-i18nify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Colors } from "@styles/colors";
import { Env } from "../../../env";
import { LProgressCircle } from "@components/LProgressCircle";
import { LFlex } from "@components";
import { Headset } from "@assets";
import { AssessmentTimeout } from "@fassessment/components/AssessmentTimeout";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { SttSourceType } from "@store/generalTypes";

const LIST_A = 0;
const LIST_B = 1;

interface TextAudio {
    text: string;
    audio: string;
}

const getTextAndAudio = (currentFlow: Flow): TextAudio => {
    const defaultValue = {
        text: translate("assessments.allWordsJustRead"),
        audio: `${Env.REACT_APP_BLOB_URL}/instructions-${getLocale()}-us/allWordsJustRead.mp3`
    };

    switch (currentFlow.name) {
        case "List A Free Recall 1":
            return defaultValue;
        case "List B Free Recall 1":
            return {
                text: translate("assessments.allWordsJustReadNewList"),
                audio: `${Env.REACT_APP_BLOB_URL}/instructions-${getLocale()}-us/allWordsJustReadNewList.mp3`
            };
        case "List A Free Recall 2":
            return {
                text: translate("assessments.allWordsVeryFirst").replace("{{blobUrl}}", Env.REACT_APP_BLOB_URL),
                audio: ""
            };
        default:
            return defaultValue;
    }
};

export const AssessmentFreeRecall = (): JSX.Element => {
    const responses = useRef<ResultType[]>([]);
    const responseTime = useAppSelector((x) => x.globalSettings.freeRecall);
    const timeout = useAppSelector((x) => x.globalSettings.freeRecallTimeout);
    const showTimer = useAppSelector((x) => x.globalSettings.showTimers);

    const [uploading, setUploading] = useState(false);
    const [promptPlayed, setPromptPlayed] = useState(false);
    const [categorizedWords, setCategorizedWords] = useState<CategorizedWords>({});

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const appInsights = useAppInsightsContext();

    const assessment = useAppSelector((x) => x.assessment);

    const [assessmentFlow] = useState(useAppSelector((x) => x.assessmentFlow));

    const currentFlow = assessmentFlow.flows[assessmentFlow.currentFlow];

    const currentListIndex = currentFlow.list === -1 ? 0 : currentFlow.list;
    const currentListWordsWithAlternate = getWordsAndAlternatesFromList(assessment.wordLists[currentListIndex].words);

    const textAudio = getTextAndAudio(currentFlow);

    const listA = assessment.wordLists[LIST_A].words;
    const listB = assessment.wordLists[LIST_B].words;

    const allTargetWords = [...listA.map((x) => x.value), ...listB.map((x) => x.value)];
    const currentListWords = uniq([...currentListWordsWithAlternate, ...allTargetWords]);

    const { bulkAssessmentResponse, uploadAssessmentStepResponse } = useApi();

    const handlerOnStartedSpeakingOverride = (): void => {
        cancelTimeoutCounter();
    };

    useEffect(() => {
        if (assessmentFlow.flows[assessmentFlow.currentFlow + 1]) {
            dispatch(updateCurrentFlow());
        }

        let newCategorizedWords: CategorizedWords = {};
        newCategorizedWords = flattenAndCategorize(
            newCategorizedWords,
            listA,
            currentFlow.list !== LIST_A && currentFlow.list !== -1
        );
        newCategorizedWords = flattenAndCategorize(
            newCategorizedWords,
            listB,
            currentFlow.list !== LIST_B && currentFlow.list !== -1
        );
        newCategorizedWords = flattenAndCategorize(newCategorizedWords, assessment.intrusions as Word[], true);

        setCategorizedWords(newCategorizedWords);
    }, []);

    const { results, startSpeechToText, stopSpeechToText, recordingVolume, stream, destroy, isRecording } =
        useCustomSpeechToText({
            context: currentListWords,
            fullAudio: true,
            handlerOnStartedSpeakingOverride
        });

    const handlePrompt = useCallback(async () => {
        if (textAudio.audio) {
            const newAudio = new Audio(textAudio.audio);
            await playAudioAsync(newAudio);

            resetTimeoutCounter();
            resetCounter();
        }

        await startSpeechToText(undefined, currentListWords, { stepName: currentFlow.name });
        setPromptPlayed(true);
    }, [stream]);

    const handleOnCounterDone = async (): Promise<void> => {
        await stopSpeechToText(true, undefined, currentListWords, {
            shouldEnd: true
        });

        setUploading(true);
    };

    const [counter, resetCounter] = useCountDown({
        from: responseTime,
        onDone: handleOnCounterDone
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, resetTimeoutCounter, cancelTimeoutCounter] = useCountDown({
        from: timeout,
        onDone: () => {
            void handleNoResponses();
        }
    });

    const [playAudio] = useAudio();

    const uploadResult = async (): Promise<void> => {
        const blobs = responses.current.map((x) => x.speechBlob) as Blob[];
        const oneBlob = await concatenateBlobs(blobs);

        if (!isEmpty(responses.current) && oneBlob) {
            const responsesWords = getUniqueWords(responses.current);
            const uniqWords = uniq(flatten(responsesWords));

            let filteredWords = filterAndMatch(uniqWords, categorizedWords);
            filteredWords = uniqWith(
                filteredWords.filter((x) => !x.response.includes(" ")),
                isEqual
            );

            const stepName = currentFlow.name;

            const assessmentId = assessment.id ?? 0;

            try {
                await bulkAssessmentResponse(assessmentId, {
                    stepName,
                    responses: filteredWords
                });
            } catch (er) {
                console.error(er);
            }

            const success = await uploadAssessmentAudioStepResponse(assessmentId, stepName, oneBlob);

            if (!success) {
                await uploadAssessmentAudioStepResponse(assessmentId, stepName, oneBlob);
            }
        }

        await sleepFor(2);
        destroy();

        if (!assessmentFlow.flows[assessmentFlow.currentFlow + 1]) {
            navigate(NavItems.Assessments.AssessmentClosingVideo.path);
        }

        navigate(assessmentFlow.flows[assessmentFlow.currentFlow + 1].path);
    };

    const uploadAssessmentAudioStepResponse = async (
        assessmentId: number,
        stepName: string,
        audio: Blob
    ): Promise<boolean> => {
        let result = true;

        try {
            await uploadAssessmentStepResponse(assessmentId, stepName, audio);
        } catch (error) {
            appInsights.trackTrace({
                message: JSON.stringify(error),
                severityLevel: SeverityLevel.Information
            });

            result = false;
            console.error(error);
        }

        return result;
    };

    const handleNoResponses = async (): Promise<void> => {
        playAudio(translate("assessments.doYourBestAudio").replace("{{blobUrl}}", Env.REACT_APP_BLOB_URL), () => {});
        resetCounter();
        cancelTimeoutCounter();
    };

    useEffect(() => {
        if (!results?.length) return;

        const googleUniqList = flatten(
            results
                .filter((x) => x.source === SttSourceType.Google)
                .map((x) => uniq(x.transcript.toLocaleLowerCase().split(" ")))
        );
        const azureUniqList = flatten(
            results
                .filter((x) => x.source === SttSourceType.Azure)
                .map((x) => uniq(x.transcript.toLocaleLowerCase().split(" ")))
        );

        const interleaved = zip(googleUniqList, azureUniqList);
        const mergedList = flatten(interleaved).filter((item) => item !== undefined);
        const joinResults = uniq(mergedList).join(" ").trim();

        const orderedResults = results.sort((x, y) => y.confidence - x.confidence);

        const highestConfidenceResult = orderedResults[0];

        const res: ResultType = {
            transcript: joinResults,
            speechBlob: highestConfidenceResult.speechBlob,
            elapsed: highestConfidenceResult.elapsed,
            intendedWord: highestConfidenceResult.intendedWord,
            confidence: highestConfidenceResult.confidence,
            context: highestConfidenceResult.context
        };

        if (res && res.transcript !== "-") {
            responses.current = [...responses.current, res];
        }

        if (res.context.shouldEnd) {
            if (isEmpty(responses.current)) {
                responses.current = [...responses.current, res];
            }

            void uploadResult();
        }
    }, [results]);

    useEffect(() => {
        if (stream && !promptPlayed) {
            void handlePrompt();
        }
    }, [stream]);

    const progress = Math.floor(((responseTime - counter) * 100) / responseTime);
    const time = new Date((responseTime - (responseTime - counter)) * 1000).toISOString().slice(14, 19);

    return (
        <AssessmentShell micIsRecording={isRecording} micVolume={recordingVolume}>
            <div style={{ paddingBottom: "6rem" }}>
                {!uploading ? (
                    <>
                        {showTimer && (
                            <div style={{ position: "absolute", right: "8%", top: "2%" }}>
                                <LProgressCircle
                                    progress={progress}
                                    text={time}
                                    labelColor={Colors.primaryBlue}
                                    trackColor={Colors.primaryBlue}
                                    indicatorColor={Colors.primaryBlue}
                                />
                                <div>{results[0]?.transcript}</div>
                            </div>
                        )}
                        <LFlex justifyCenter alignCenter>
                            <LSubTitle size="1" color="has-text-dark-blue">
                                {textAudio.text}
                                <p className="is-size-1 has-text-dark-blue">
                                    <Translate value="assessments.doYourBest" />
                                </p>
                            </LSubTitle>
                            <LFlex className={isRecording ? "" : "is-hidden-custom"} row alignCenter justifyCenter>
                                <Headset />
                                <p className="mx-4 has-text-dark-blue">
                                    <Translate value="assessments.listening" />
                                    ...
                                </p>
                            </LFlex>
                        </LFlex>
                    </>
                ) : (
                    <div>
                        <LSubTitle size="1" color="has-text-dark-blue">
                            <Translate value="assessments.pleaseWait" />
                        </LSubTitle>
                        <FontAwesomeIcon icon={faSpinner} size="6x" color={Colors.darkBlue} className="fa-spin" />
                    </div>
                )}
            </div>
            <AssessmentTimeout micVolume={recordingVolume} />
        </AssessmentShell>
    );
};
